import React, { createContext } from 'react'
import PropTypes from 'prop-types'
import * as Scroll from 'react-scroll'
import styled from 'styled-components'
import { Box } from 'reflexbox/styled-components'
import camelCase from 'lodash/camelCase'

import baseTheme from 'theme'

import {
  classString,
  getThemeLength,
  calculateLength,
  getPaddingValues,
} from 'helpers'
import { getModeStyling, HIGHLIGHT_COLOR } from './helpers'

import { CLASS_NAME as SUBSECTION_CLASS_NAME } from './SubSection'
import { MOBILE_PADDING, PADDING_X, PADDING_TOP } from './constants'

import useVignette, { TOP_RIGHT, BOTTOM_LEFT } from 'hooks/useVignette'

export const TYPE_SECTION = 'section'
export const TYPE_SECTION_BREAK = 'section-break'
export const TYPE_LOGO_BAR = 'logo-bar'

export const NAME_DEFAULT = 'section'

const getMargin = (type) => {
  switch (type) {
    case TYPE_LOGO_BAR:
      return '-30px auto 0'
    default:
      return '0 auto 30px'
  }
}

const getSectionAttrs = ({ as, type, theme }) => ({
  as,
  p:
    type === TYPE_LOGO_BAR
      ? 0
      : type === TYPE_SECTION_BREAK
        ? theme?.components?.sectionBreak?.padding || ['30px'] // TODO: urgent! support syntax with 2 values
        : ['0 0 30px'],
  mt: type === TYPE_LOGO_BAR || type === TYPE_SECTION_BREAK ? '-30px' : null,
})

// should the padding here be equal to the constants below...?
export const Section = styled(Box)
  .attrs(getSectionAttrs)
  .withConfig({
    shouldForwardProp: (prop) => ['type'].indexOf(prop) !== -1,
  })`
    max-width: ${baseTheme.paperMaxWidth}px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    & ::selection {
      background: ${HIGHLIGHT_COLOR};
      color: ${baseTheme.colors.black};
    } 

      ${({ type }) =>
        type === TYPE_SECTION_BREAK &&
        `
        & > h1, 
        & > h2,
        & > h3,
        & > h4,
        & > h5,
        & > h6 {
          width: fit-content;
          margin-left: auto;
          margin-right: auto;
        }

        && > * {
          text-align: center;
          width: fit-content;
          margin-left: auto;
          margin-right: auto;
        }

        & > p {
          margin: 20px auto;
        }
      `}
  `

const getAttrs = ({
  type,
  as,
  theme,
  additionalPaddingBottom,
  additionalDesktopPaddingBottom,
  additionalMobilePaddingBottom,
}) => {
  const mobilePadding = getThemeLength({
    theme,
    key: 'padding',
    component: TYPE_SECTION,
    breakpoint: 'mobile',
  })

  const {
    paddingRight: mobilePaddingRight,
    paddingLeft: mobilePaddingLeft,
    paddingTop: mobilePaddingTop,
    paddingBottom: mobilePaddingBottom,
  } = getPaddingValues(mobilePadding, MOBILE_PADDING)

  const actualMobilePaddingBottom = calculateLength(
    mobilePaddingBottom,
    '+',
    additionalMobilePaddingBottom
  )

  const tabletPadding = getThemeLength({
    theme,
    key: 'padding',
    component: TYPE_SECTION,
    breakpoint: 'tablet',
  })

  const {
    paddingRight: tabletPaddingRight,
    paddingLeft: tabletPaddingLeft,
    paddingTop: tabletPaddingTop,
    paddingBottom: tabletPaddingBottom,
  } = getPaddingValues(tabletPadding, MOBILE_PADDING)

  const desktopPadding = getThemeLength({
    theme,
    key: 'padding',
    component: TYPE_SECTION,
    breakpoint: 'desktop',
  })

  const {
    paddingRight: desktopPaddingRight,
    paddingLeft: desktopPaddingLeft,
    paddingTop: desktopPaddingTop,
    paddingBottom: desktopPaddingBottom,
  } = getPaddingValues(desktopPadding, {
    paddingX: PADDING_X,
    paddingY: PADDING_TOP,
  })

  const paddingDesktopBottom = calculateLength(
    desktopPaddingBottom,
    '+',
    additionalDesktopPaddingBottom
  )

  const padding = getThemeLength({
    theme,
    key: 'padding',
    component: TYPE_SECTION,
  })

  const { paddingRight, paddingLeft, paddingTop, paddingBottom } =
    getPaddingValues(padding, {
      paddingX: PADDING_X,
      paddingY: PADDING_TOP,
    })

  const paddingGiantBottom = calculateLength(
    paddingBottom,
    '+',
    additionalPaddingBottom
  )

  return {
    as,
    pr: [
      mobilePaddingRight,
      tabletPaddingRight,
      desktopPaddingRight,
      paddingRight,
    ],
    pl: [mobilePaddingLeft, tabletPaddingLeft, desktopPaddingLeft, paddingLeft],
    pt: [mobilePaddingTop, tabletPaddingTop, desktopPaddingTop, paddingTop],
    pb: [
      actualMobilePaddingBottom,
      tabletPaddingBottom,
      paddingDesktopBottom,
      paddingGiantBottom,
    ],
    m: getMargin(type),
  }
}

// NOTE: overflow: hidden causes a good amount of problems...
// it was a holdover from Paper.Container, we kinda depend on how it is now
// but if we could change it and make this component "just work" in many
// diff contexts, that would be amazing
const PaperContainer = styled(Box)
  .attrs(getAttrs)
  .withConfig({
    shouldForwardProp: (prop) => ['mode', 'background'].indexOf(prop) === -1,
  })`
  position: relative;
  min-width: 0px;
  max-width: ${baseTheme.paperMaxWidth}px;
  width: 100%;

  height: min-content;

  ${({ mode, theme, background }) => getModeStyling({ mode, theme, background })}

  box-shadow: 0px -4px 15px rgba(0, 0, 0, 0.08);

  overflow: hidden;

  // if the VERY FIRST child is a subsection, remove the top margin only
  & > *:first-child:is(.${SUBSECTION_CLASS_NAME}) {
    margin-top: 0;
  }

  // if the VERY FIRST child is a subsection, remove the top margin and padding from the section.
  // shorter way to say this: let the subsection manage its own padding
  :has(> *:first-child:is(.${SUBSECTION_CLASS_NAME})) {
    padding-top: 0;
    margin-top: 0;
  }
`

export const SectionContext = createContext({
  type: TYPE_SECTION,
  name: NAME_DEFAULT,
})

// TODO: bake in changing the tag of an element to 'main' if it's the main part. Probably always the hero? Maybe a demo section?
// could expose a prop like isMainSection or something, and then force validation on the page Layout to ensure only one section
// is marked as main
function NewSection (
  {
    children,
    type,
    name = NAME_DEFAULT, // dunno why defaultProps isnt working...
    mode,
    scrollName,
    className,
    vignette,
    // TODO: clean up these props forrrrreal
    additionalPaddingBottom,
    additionalDesktopPaddingBottom,
    additionalMobilePaddingBottom,
    ...others
  },
  ref
) {
  const dontRenderPaperContainer =
    type === 'logo-bar' || type === 'section-break'

  // render different semantic html depending on the type
  const as = type === 'logo-bar' ? 'aside' : 'section'

  const contextValue = {
    type: camelCase(type),
    name,
    mode,
  }

  // defaults to top-right vignette as long as the prop is at least true
  const vignetteElement = useVignette(vignette)

  const renderChildren = () =>
    dontRenderPaperContainer ? (
      <Section
        type={type}
        className={classString([className, 'anvil-paper-section'])}
        as={as}
        {...others}
        ref={ref}
      >
        {children}
      </Section>
    ) : (
      <PaperContainer
        type={type}
        className={classString([className, 'anvil-paper-section'])}
        as={as}
        additionalDesktopPaddingBottom={additionalDesktopPaddingBottom}
        additionalPaddingBottom={additionalPaddingBottom}
        {...others}
        ref={ref}
      >
        {children}
        {vignetteElement}
      </PaperContainer>
    )

  const SectionWrapper = scrollName
    ? (props) => <Scroll.Element name={scrollName} id={scrollName} {...props} />
    : React.Fragment

  return (
    <SectionWrapper>
      <SectionContext.Provider value={contextValue}>
        {renderChildren()}
      </SectionContext.Provider>
    </SectionWrapper>
  )
}

export const plasmicProps = {
  children: 'slot',
  type: {
    type: 'choice',
    options: ['logo-bar', 'last'],
  },
  scrollName: 'string',
}

NewSection.defaultProps = {
  mode: 'light',
  type: TYPE_SECTION,
  name: NAME_DEFAULT,
}

NewSection.propTypes = {
  children: PropTypes.node, // TODO: update this to whatever children actually is
  // assumption: 'logo-bar' types are always after a section with padding-bottom of '30px'
  type: PropTypes.oneOf([TYPE_LOGO_BAR, TYPE_SECTION_BREAK, TYPE_SECTION]), // would be cool to make `Section` context aware, and have it automatically set this prop...
  name: PropTypes.string.isRequired,
  // TODO: mode should be deprecated, in favor of 'background' that takes colors from the theme
  // and uses theme a key to set color, link color, etc
  mode: PropTypes.oneOf(['light', 'dark', 'blue', 'green', 'yellow']),
  scrollName: PropTypes.string,
  className: PropTypes.string,
  vignette: PropTypes.shape({
    position: PropTypes.oneOf([TOP_RIGHT, BOTTOM_LEFT]),
    color: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
  }),

  // these two must but a string with a unit, like '30px'
  additionalPaddingBottom: PropTypes.string,
  additionalMobilePaddingBottom: PropTypes.string,
}

// TODO: compute when the section is the last one

export default React.forwardRef(NewSection)
